import { api, type InfoShareReplySummary } from '../api/client';
import { decryptOwnerReply, decryptReplyAttachmentFile, decryptReplyAttachmentName } from '../crypto/infoShareReply';
import type { InfoShareManifestPlaintext } from '../crypto/infoShareFragment';
import { getStoredInfoShareReplyPrivateKey } from '../crypto/infoShareReplyKeys';
import { decodeBase64Url } from '../crypto/primitives';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { registerRepliesDrawer } from '../ws/infoShareLive';
import { setCryptoBusy } from './cryptoBusy';
import { bindModalDismiss, createModalShell } from './modalDialogShell';
import { inferContentTypeFromFileName, fileTypeIconFor } from '../files/fileTypes';
import { fileTypeImg } from './fileTypeImg';
import { showFilePreview } from './filePreviewDialog';

interface CachedReply {
  text: string;
  attachments: InfoShareManifestPlaintext['attachments'];
  contentKey: Uint8Array;
  purgeAt: string | null;
  opened: boolean;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export interface RepliesDrawerController {
  shareId: string;
  addReply(reply: InfoShareReplySummary): void;
  updateReply(replyId: string, patch: { opened?: boolean; purgeAt?: string | null }): void;
  close(): void;
}

function formatReplyExpiry(purgeAt: string | null): string {
  if (!purgeAt) {
    return '';
  }

  return `${t('info.owner.replyExpiresLabel')} ${new Date(purgeAt).toLocaleString()}`;
}

function setRowExpiry(row: HTMLElement, purgeAt: string | null): void {
  const expiresEl = row.querySelector('.reply-row__expires') as HTMLSpanElement;
  if (!purgeAt) {
    expiresEl.hidden = true;
    expiresEl.textContent = '';
    return;
  }

  expiresEl.textContent = formatReplyExpiry(purgeAt);
  expiresEl.hidden = false;
}

function setRowReadState(row: HTMLElement, opened: boolean): void {
  row.classList.toggle('reply-row--unread', !opened);
  row.classList.toggle('reply-row--read', opened);

  const badge = row.querySelector('.reply-row__status') as HTMLSpanElement;
  badge.classList.toggle('status-badge--unread', !opened);
  badge.classList.toggle('status-badge--read', opened);
  badge.textContent = opened ? t('info.owner.replyRead') : t('info.owner.replyUnread');
}

function setRowExpanded(row: HTMLElement, expanded: boolean): void {
  row.classList.toggle('reply-row--expanded', expanded);
  const body = row.querySelector('.reply-row__body') as HTMLDivElement;
  const toggle = row.querySelector('.reply-row__toggle') as HTMLButtonElement;
  body.hidden = !expanded;
  toggle.textContent = expanded ? t('info.owner.replyClose') : t('info.owner.replyOpen');
}

export async function openInfoShareRepliesDrawer(
  shareId: string,
  ownerUserId: string,
  onUnreadChanged: (shareId: string, unreadCount: number) => void,
): Promise<void> {
  let replies: InfoShareReplySummary[];
  try {
    replies = await api.listInfoShareReplies(shareId);
  } catch (cause) {
    console.error('Load replies failed', cause);
    return;
  }

  const cache = new Map<string, CachedReply>();
  const rowById = new Map<string, HTMLElement>();

  const shell = createModalShell({
    titleKey: 'info.owner.repliesTitle',
    helpTopic: dialogHelpTopic('infoShareReplies'),
    className: 'replies-dialog',
  });

  shell.body.innerHTML = `
    <div id="replies-list" class="replies-list"></div>
    <p class="error-text" id="replies-error" hidden></p>
  `;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'button ghost';
  closeButton.id = 'replies-close';
  closeButton.innerHTML = '<span data-i18n="info.owner.dialogClose"></span>';
  shell.actions.append(closeButton);

  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const list = shell.body.querySelector('#replies-list') as HTMLDivElement;
  const error = shell.body.querySelector('#replies-error') as HTMLParagraphElement;

  const refreshLocale = () => {
    for (const [replyId, row] of rowById) {
      const cached = cache.get(replyId);
      const reply = replies.find((entry) => entry.id === replyId);
      const opened = cached?.opened ?? reply?.opened ?? false;
      setRowReadState(row, opened);

      const purgeAt = cached?.purgeAt ?? reply?.purgeAt ?? null;
      setRowExpiry(row, purgeAt);

      const expanded = row.classList.contains('reply-row--expanded');
      setRowExpanded(row, expanded);
    }

    if (!error.hidden && error.dataset.errorKey) {
      error.textContent = t(error.dataset.errorKey);
    }
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    unbindLocale();
    unbindDismiss();
    registerRepliesDrawer(null);
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  closeButton.addEventListener('click', close);

  const privateKey = getStoredInfoShareReplyPrivateKey(shareId);
  if (!privateKey) {
    error.hidden = false;
    error.dataset.errorKey = 'info.owner.replyKeyMissing';
    error.textContent = t('info.owner.replyKeyMissing');
  }

  function removeEmptyState(): void {
    list.querySelector('[data-replies-empty]')?.remove();
  }

  function showEmptyState(): void {
    if (list.querySelector('[data-replies-empty]')) {
      return;
    }

    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.dataset.repliesEmpty = 'true';
    empty.setAttribute('data-i18n', 'info.owner.repliesEmpty');
    list.append(empty);
    applyI18n(empty);
  }

  function mountReplyRow(reply: InfoShareReplySummary, position: 'prepend' | 'append' = 'append'): HTMLElement {
    removeEmptyState();

    const row = document.createElement('article');
    row.className = `reply-row ${reply.opened ? 'reply-row--read' : 'reply-row--unread'}`;
    row.dataset.replyId = reply.id;
    row.innerHTML = `
      <div class="reply-row__header">
        <div class="reply-row__meta">
          <span class="status-badge reply-row__status ${reply.opened ? 'status-badge--read' : 'status-badge--unread'}"></span>
          <span class="muted reply-row__received">${new Date(reply.createdAt).toLocaleString()}</span>
          <span class="reply-row__expires muted" hidden></span>
        </div>
        <button type="button" class="button ghost reply-row__toggle">
          <span data-i18n="info.owner.replyOpen"></span>
        </button>
      </div>
      <div class="reply-row__body" hidden>
        <pre class="reply-row__text message-display guest-message-text"></pre>
        <ul class="reply-row__attachments" hidden></ul>
      </div>
    `;

    setRowReadState(row, reply.opened);
    if (reply.purgeAt) {
      setRowExpiry(row, reply.purgeAt);
    }

    const toggle = row.querySelector('.reply-row__toggle') as HTMLButtonElement;
    const textEl = row.querySelector('.reply-row__text') as HTMLPreElement;
    const attachmentsEl = row.querySelector('.reply-row__attachments') as HTMLUListElement;

    const renderAttachments = async (
      attachments: InfoShareManifestPlaintext['attachments'],
      contentKey: Uint8Array,
    ) => {
      attachmentsEl.replaceChildren();
      if (attachments.length === 0) {
        attachmentsEl.hidden = true;
        return;
      }

      attachmentsEl.hidden = false;
      for (const attachment of attachments) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button ghost';
        let attachmentFileName = '';
        try {
          attachmentFileName = await decryptReplyAttachmentName(contentKey, attachment.encryptedName);
          const icon = fileTypeImg(fileTypeIconFor(
            attachmentFileName,
            inferContentTypeFromFileName(attachmentFileName),
          ), 16);
          button.append(icon, document.createTextNode(attachmentFileName));
        } catch {
          button.textContent = t('info.owner.replyAttachment');
        }
        button.addEventListener('click', () => {
          void (async () => {
            if (!attachmentFileName) {
              error.hidden = false;
              error.dataset.errorKey = 'info.owner.replyAttachmentFailed';
              error.textContent = t('info.owner.replyAttachmentFailed');
              return;
            }
            try {
              const bytes = await decryptReplyAttachmentFile(contentKey, attachment);
              const blob = new Blob([toArrayBuffer(bytes)], {
                type: inferContentTypeFromFileName(attachmentFileName) ?? 'application/octet-stream',
              });
              await showFilePreview(attachmentFileName, blob);
            } catch (cause) {
              console.error('Reply attachment preview failed', cause);
              error.hidden = false;
              error.dataset.errorKey = 'info.owner.replyAttachmentFailed';
              error.textContent = t('info.owner.replyAttachmentFailed');
            }
          })();
        });
        item.append(button);
        attachmentsEl.append(item);
      }
    };

    const showCachedReply = async (cached: CachedReply) => {
      textEl.textContent = cached.text;
      await renderAttachments(cached.attachments, cached.contentKey);
      setRowExpiry(row, cached.purgeAt);
      setRowReadState(row, cached.opened);
      setRowExpanded(row, true);
    };

    toggle.addEventListener('click', () => {
      void (async () => {
        if (row.dataset.busy === 'true') {
          return;
        }

        const isExpanded = row.classList.contains('reply-row--expanded');

        if (isExpanded) {
          setRowExpanded(row, false);
          return;
        }

        if (!privateKey) {
          error.hidden = false;
          error.dataset.errorKey = 'info.owner.replyKeyMissing';
          error.textContent = t('info.owner.replyKeyMissing');
          return;
        }

        error.hidden = true;

        const cached = cache.get(reply.id);
        if (cached) {
          await showCachedReply(cached);
          return;
        }

        row.dataset.busy = 'true';
        setCryptoBusy(row, toggle, true, {
          titleKey: 'info.owner.replyDecrypting',
          detailKey: 'info.owner.replyDecryptingDetail',
          icon: 'hourglass',
        });

        try {
          const detail = await api.getInfoShareReply(shareId, reply.id);
          const manifestBytes = await api.fetchInfoShareReplyManifest(shareId, reply.id);
          const { manifest, contentKey } = await decryptOwnerReply({
            wrappedContentKey: decodeBase64Url(detail.wrappedContentKey),
            manifestBlob: manifestBytes,
            replyPrivateKey: privateKey,
            infoShareId: shareId,
            ownerUserId,
          });

          let purgeAt = detail.purgeAt;
          let opened = detail.opened;

          if (!detail.opened) {
            const openedResponse = await api.markInfoShareReplyOpened(shareId, reply.id);
            purgeAt = openedResponse.purgeAt;
            opened = true;
            const item = replies.find((entry) => entry.id === reply.id);
            if (item) {
              item.opened = true;
            }
            onUnreadChanged(shareId, replies.filter((entry) => !entry.opened).length);
          }

          cache.set(reply.id, {
            text: manifest.text,
            attachments: manifest.attachments,
            contentKey,
            purgeAt,
            opened,
          });

          await showCachedReply(cache.get(reply.id)!);
        } catch (cause) {
          console.error('Open reply failed', cause);
          error.hidden = false;
          error.dataset.errorKey = 'status.error';
          error.textContent = t('status.error');
        } finally {
          row.dataset.busy = 'false';
          setCryptoBusy(row, toggle, false, {
            titleKey: 'info.owner.replyDecrypting',
            detailKey: 'info.owner.replyDecryptingDetail',
          });
        }
      })();
    });

    if (position === 'prepend') {
      list.prepend(row);
    } else {
      list.append(row);
    }

    rowById.set(reply.id, row);
    applyI18n(row);
    return row;
  }

  const controller: RepliesDrawerController = {
    shareId,
    addReply: (reply) => {
      if (rowById.has(reply.id)) {
        return;
      }

      replies = [reply, ...replies];
      mountReplyRow(reply, 'prepend');
    },
    updateReply: (replyId, patch) => {
      const row = rowById.get(replyId);
      if (!row || row.dataset.busy === 'true' || row.classList.contains('reply-row--expanded')) {
        return;
      }

      if (patch.opened !== undefined) {
        setRowReadState(row, patch.opened);
        const cached = cache.get(replyId);
        if (cached) {
          cached.opened = patch.opened;
        }
      }

      if (patch.purgeAt !== undefined) {
        setRowExpiry(row, patch.purgeAt);
        const cached = cache.get(replyId);
        if (cached) {
          cached.purgeAt = patch.purgeAt;
        }
      }
    },
    close,
  };

  registerRepliesDrawer(controller);

  if (replies.length === 0) {
    showEmptyState();
    return;
  }

  for (const reply of replies) {
    mountReplyRow(reply);
  }
}
